'use client'

import { useState, useRef, useEffect, type MutableRefObject } from 'react'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import TicketSelector from './TicketSelector'
import BookingSummary from './BookingSummary'
import LoadingSpinner from '../common/LoadingSpinner'
import type { TimedUITicketType, DayTabConfig } from '@/lib/timed-ui/adapters'

interface MobileBookingSheetProps {
  ticketTypes: TimedUITicketType[]
  regularTickets: TimedUITicketType[]
  selectedTickets: Record<string, number>
  onTicketQuantityChange: (ticketTypeId: string, quantity: number) => void
  totalPrice: number
  ticketCount: number
  onCheckout: () => void
  loadingTickets: boolean
  checkoutLoading: boolean
  slotCapacity?: number
  /** Cap across all ticket types in the order (a sponsor link's limit). */
  orderLimit?: number | null
  dayTabConfig?: DayTabConfig | null
  timezone?: string
  expandRef?: MutableRefObject<(() => void) | null>
  autoExpand?: boolean
}

const MobileBookingSheet = ({
  ticketTypes,
  regularTickets,
  selectedTickets,
  onTicketQuantityChange,
  totalPrice,
  ticketCount,
  onCheckout,
  loadingTickets,
  checkoutLoading,
  slotCapacity,
  orderLimit,
  dayTabConfig,
  timezone,
  expandRef,
  autoExpand = true,
}: MobileBookingSheetProps) => {
  const formatPrice = useFormatPrice()
  const [isExpanded, setIsExpanded] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const showTickets = ticketTypes.length > 0 || loadingTickets
  const hasTickets = ticketCount > 0

  const totalItemCount = ticketCount

  useEffect(() => {
    if (autoExpand === false) return
    const mql = window.matchMedia('(max-width: 1023px)')
    if (mql.matches) {
      setIsExpanded(true)
    }
  }, [autoExpand])

  // Expose expand function to parent via ref
  useEffect(() => {
    if (expandRef) {
      expandRef.current = () => setIsExpanded(true)
    }
    return () => {
      if (expandRef) expandRef.current = null
    }
  }, [expandRef])

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches
    if (isExpanded && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isExpanded])

  return (
    <>
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
          onClick={() => setIsExpanded(false)}
        />
      )}

      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-surface-border shadow-sticky transition-[max-height,border-radius] duration-300 ease-out ${
          isExpanded ? 'rounded-t-2xl' : 'max-h-[140px]'
        }`}
      >
        {isExpanded ? (
          <div className="flex flex-col" style={{ maxHeight: 'min(calc(100svh - 4rem), calc(100dvh - 7rem))' }}>
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-surface-border shrink-0">
              <h2 className="text-lg font-heading font-bold text-text-primary">
                Book Your Experience
              </h2>
              <button
                onClick={() => setIsExpanded(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-surface-tertiary transition-colors"
                aria-label="Close booking panel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 pb-6 space-y-6">
              {showTickets && (
                <div className="pt-4 border-t border-surface-border animate-slide-up">
                  <h3 className="text-base font-heading font-bold text-text-primary mb-3">
                    Select Tickets
                  </h3>
                  {loadingTickets ? (
                    <div className="py-8 min-h-[120px] flex items-center justify-center">
                      <LoadingSpinner size="sm" text="Loading tickets..." />
                    </div>
                  ) : (
                    <TicketSelector
                      ticketTypes={regularTickets.length > 0 ? regularTickets : ticketTypes}
                      selectedTickets={selectedTickets}
                      onQuantityChange={onTicketQuantityChange}
                      slotCapacity={slotCapacity}
                orderLimit={orderLimit}
                      dayTabConfig={dayTabConfig}
                      timezone={timezone}
                    />
                  )}
                </div>
              )}

              {showTickets && (
                <BookingSummary
                  totalPrice={totalPrice}
                  ticketCount={ticketCount}
                />
              )}
            </div>

            {totalItemCount > 0 && (
              <div className="shrink-0 border-t border-surface-border bg-surface px-4 pt-4" style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}>
                <button
                  onClick={onCheckout}
                  disabled={checkoutLoading}
                  className="w-full bg-primary hover:bg-primary-hover text-text-inverse font-bold py-4 rounded-xl text-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-sm"
                >
                  {checkoutLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <span>Checkout</span>
                      <span className="bg-text-inverse/20 px-2.5 py-0.5 rounded-lg text-sm">
                        {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'} · {formatPrice(totalPrice)}
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            className={`flex items-center justify-between gap-3 px-5 pt-4 transition-colors duration-300 ${hasTickets ? 'bg-primary/5' : ''}`}
            style={{ paddingBottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
          >
            <button
              onClick={() => setIsExpanded(true)}
              className="text-left flex-1 min-w-0"
            >
              {totalItemCount > 0 ? (
                <>
                  <div className="text-xs text-text-secondary">
                    {totalItemCount} {totalItemCount === 1 ? 'item' : 'items'} · <span className="underline">edit</span>
                  </div>
                  <div className="text-lg font-heading font-bold text-text-primary">
                    {formatPrice(totalPrice)}
                  </div>
                </>
              ) : (
                <div className="text-sm text-text-secondary">
                  Choose tickets
                </div>
              )}
            </button>

            <button
              onClick={totalItemCount > 0 ? onCheckout : () => setIsExpanded(true)}
              disabled={checkoutLoading}
              className="px-6 py-3 text-base font-bold rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-60 shrink-0 bg-primary text-text-inverse hover:bg-primary-hover shadow-sm"
            >
              {checkoutLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : totalItemCount > 0 ? (
                <span className="flex items-center gap-2">
                  Checkout
                  <span className="bg-text-inverse/20 px-2 py-0.5 rounded-lg text-sm">{totalItemCount}</span>
                </span>
              ) : 'Get Tickets'}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default MobileBookingSheet
